import type { EventGroup, EventFormValues } from '../types/events.types';
import type { Event, EventRsvp, UserRole } from '../types/database';

/**
 * Format a date string for use as a section header
 * Example: "November 8, 2024"
 *
 * Parses the date string (YYYY-MM-DD) as a local date to avoid timezone issues.
 * When parsing "2024-11-23", we want it to display as November 23 regardless of timezone.
 */
export function formatDateHeader(dateString: string): string {
  // Parse YYYY-MM-DD format and create a date in local timezone
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format an event's start time for display
 * Example: "Mon - October 8th - 3:00pm"
 */
export function formatEventDateTime(startTime: string): string {
  const date = new Date(startTime);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const day = date.getDate();
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${weekday} - ${month} ${day}${getOrdinalSuffix(day)} - ${time}`;
}

/**
 * Get the ordinal suffix for a day number
 * Examples: 1st, 2nd, 3rd, 4th, 21st, etc.
 */
function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/**
 * Get the display text for an RSVP status
 */
export function getRsvpStatusText(
  status: 'yes' | 'no' | 'attended' | null
): string {
  switch (status) {
    case 'yes':
      return 'Going';
    case 'no':
      return 'Not going';
    case 'attended':
      return 'Attended';
    default:
      return 'RSVP Required';
  }
}

/**
 * Get the Tailwind color class for an RSVP status indicator
 */
export function getRsvpStatusColor(
  status: 'yes' | 'no' | 'attended' | null
): string {
  switch (status) {
    case 'yes':
      return 'bg-sky-400';
    case 'no':
      return 'bg-red-400';
    case 'attended':
      return 'bg-green-400';
    default:
      return 'bg-slate-400';
  }
}

/**
 * Group events by date and sort them appropriately
 * @param events - Array of events to group
 * @param isPast - Whether these are past events (affects sort order)
 * @returns Array of event groups, each containing a date and its events
 */
export function groupEventsByDate(
  events: Event[],
  isPast: boolean = false
): EventGroup[] {
  const grouped = new Map<string, Event[]>();

  events.forEach((event) => {
    const eventDate = new Date(event.start_time);
    // Use local date methods to get YYYY-MM-DD in user's timezone
    // (toISOString() would return UTC, causing timezone mismatches)
    const year = eventDate.getFullYear();
    const month = String(eventDate.getMonth() + 1).padStart(2, '0');
    const day = String(eventDate.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(event);
  });

  // Convert to array and sort events within each group
  const groups = Array.from(grouped.entries()).map(([date, events]) => ({
    date,
    events: events.sort((a, b) => {
      const aTime = new Date(a.start_time).getTime();
      const bTime = new Date(b.start_time).getTime();
      return isPast ? bTime - aTime : aTime - bTime;
    }),
  }));

  // Sort groups by date (ascending for upcoming, descending for past)
  return groups.sort((a, b) => {
    if (isPast) {
      return b.date.localeCompare(a.date); // Newest first for past events
    }
    return a.date.localeCompare(b.date); // Oldest first for upcoming events
  });
}

/**
 * Format an event date for display
 * Example: "Nov 12, 2024"
 */
export function formatEventDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format an event time range for display
 * Example: "5:00pm - 8:30pm"
 */
export function formatEventTimeRange(
  startTime: string,
  endTime: string
): string {
  const start = new Date(startTime);
  const end = new Date(endTime);

  const startTimeStr = start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const endTimeStr = end.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${startTimeStr} - ${endTimeStr}`;
}

/**
 * Format RSVP deadline for display
 * Example: "RSVP by Fri, November 8th"
 */
export function formatRsvpDeadline(deadline: string): string {
  const date = new Date(deadline);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const day = date.getDate();

  return `RSVP by ${weekday}, ${month} ${day}${getOrdinalSuffix(day)}`;
}

/**
 * Check if the RSVP deadline has passed
 */
export function isRsvpDeadlinePassed(deadline: string | null): boolean {
  if (!deadline) return false;
  return new Date(deadline) < new Date();
}

/**
 * Check if an event is at capacity
 */
export function isEventAtCapacity(event: Event, rsvps: EventRsvp[]): boolean {
  if (!event.capacity) return false;
  const yesRsvps = rsvps.filter((rsvp) => rsvp.status === 'yes').length;
  return yesRsvps >= event.capacity;
}

/**
 * Format a datetime string for calendar export (date part)
 * Returns YYYY-MM-DD format required by add-to-calendar-button
 * Example: "2024-11-23"
 */
export function formatDateForCalendar(isoString: string): string {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a datetime string for calendar export (time part)
 * Returns HH:MM format required by add-to-calendar-button
 * Example: "14:30"
 */
export function formatTimeForCalendar(isoString: string): string {
  const date = new Date(isoString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Convert an ISO datetime to a time input value (HH:MM).
 */
export function formatTimeForInput(isoString: string): string {
  const date = new Date(isoString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Extract the local date portion from a date-only or ISO datetime value.
 */
export function extractDateOnly(isoString: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoString)) {
    return isoString;
  }

  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Default values for the event form
 */
export const eventFormDefaultValues: EventFormValues = {
  title: '',
  description: '',
  location: '',
  eventDate: '',
  startTime: '',
  endTime: '',
  eventType: '',
  rsvpDeadline: '',
  capacity: undefined,
  invitationGroups: new Set<UserRole>(),
  eventGroupTargetIds: new Set<string>(),
  eventLink: '',
  linkName: '',
  recurrenceType: 'none',
  recurrenceCount: 2,
};

/**
 * CSS classes for form elements
 */
export const eventFormClasses = {
  label: 'text-large text-slate-50',
  input: 'border-purple-400',
} as const;

/**
 * Event type options for the form combobox
 * These are preset suggestions, but users can enter any custom value
 */
export const eventTypeOptions = [
  { value: 'creative', label: 'Creative' },
  { value: 'practical', label: 'Practical' },
  { value: 'course', label: 'Course' },
  { value: 'module', label: 'Module' },
  { value: 'social', label: 'Social' },
  { value: 'master class', label: 'Master Class' },
] as { value: string; label: string }[];
